/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ResourceAdminState } from '@app/app/page-modules/resource/store/admin/resource-admin.state';
import { Select } from '@ngxs/store';
import { Observable, takeUntil } from 'rxjs';
import { Form, TextBoxFormContent } from '../../../../models';

@Component({
  selector: 'ptl-form-private-note',
  templateUrl: './form-private-note.component.html',
  styleUrls: ['../form.component.scss'],
})
export class FormPrivateNoteComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  /** Receives the input data object */
  @Input() form: Form;
  @Input() canRemoveItem?: boolean;
  @Input() position: number | string;

  /** Emits content data on saveForm() */
  @Output() formElementAdded = new EventEmitter<Form>();

  /** Emits removing event of this form with index */
  @Output() formElementRemoved = new EventEmitter<void>();
  @Output() formElementUpdated = new EventEmitter<Form<TextBoxFormContent>>();

  @ViewChild('formTitleInput') private formTitleInput: ElementRef<HTMLInputElement>;

  @Select(ResourceAdminState.contentStateChanged)
  contentStateChanged$: Observable<boolean>;

  textboxForm: FormGroup;
  expanded = true;
  hasError = false;
  focused = false;
  saveInProgress = false;
  forceExpand = false;

  private subscriptionEnd$ = new EventEmitter<void>();

  constructor(private fb: FormBuilder) {
    this.textboxForm = this.fb.group({
      title: ['', [Validators.required]],
      answerType: ['RICH', [Validators.required]],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.form && !!this.form.uid) {
      this.setFormContent(this.form.content as TextBoxFormContent, this.form.newAddedForm);
    }
  }

  ngOnInit() {
    const content = this.form ? (this.form.content as TextBoxFormContent) : null;
    if (this.form && (!!this.form?.uid || !!content?.title)) {
      this.setFormContent(content, this.form.newAddedForm);
    }

    this.contentStateChanged$.pipe(takeUntil(this.subscriptionEnd$)).subscribe(() => {
      this.saveInProgress = false;
    });
    this.forceExpand = this.form?.content?.forceExpand;
  }

  ngAfterViewInit(): void {
    const titleInput = this.formTitleInput?.nativeElement;
    if (this.forceExpand && titleInput) {
      setTimeout(() => {
        const titleLength = titleInput.value.length;
        titleInput.focus();
        titleInput.setSelectionRange(titleLength, titleLength);
      });
    }
  }

  ngOnDestroy() {
    this.subscriptionEnd$?.emit();
  }

  saveForm() {
    this.hasError = false;
    if (this.textboxForm.valid) {
      this.expanded = false;
      this.forceExpand = false;
      this.emitFormUpdate();
      if (!this.form?.uid) {
        this.outputData();
      } else {
        this.formElementAdded.emit(this.form);
      }
      this.saveInProgress = false;
    } else {
      this.hasError = true;
      this.saveInProgress = false;
    }
  }

  expandForm() {
    this.expanded = true;
  }

  collapseForm(event: PointerEvent) {
    event.stopPropagation();
    this.expanded = false;
    this.forceExpand = false;
  }

  outputData() {
    const outputData: Form = {
      ...this.form,
      newAddedForm: false,
      content: {
        ...this.textboxForm.value,
        type: 'PRIVATE_NOTE',
      } as TextBoxFormContent,
    };

    this.formElementAdded.emit(outputData);
  }

  removeForm() {
    this.formElementRemoved.emit();
  }

  private setFormContent(content: TextBoxFormContent, newAddedForm: boolean) {
    this.expanded = newAddedForm ?? false;
    this.textboxForm.setValue({
      title: content.title,
      answerType: 'RICH',
    });
  }

  private emitFormUpdate(): void {
    if (!this.form || !this.textboxForm) {
      return;
    }

    const value = this.textboxForm.getRawValue();
    const currentContent = (this.form.content as TextBoxFormContent) ?? ({} as TextBoxFormContent);
    const payload: Form<TextBoxFormContent> = {
      ...(this.form as Form<TextBoxFormContent>),
      newAddedForm: this.form?.uid ? false : (this.form?.newAddedForm ?? false),
      content: {
        ...currentContent,
        ...value,
        type: 'PRIVATE_NOTE',
        forceExpand: this.forceExpand,
        submitted: currentContent?.submitted ?? false,
        formIsActive: currentContent?.formIsActive ?? false,
        wordLimit: currentContent?.wordLimit ?? 0,
        minWordsLimit: currentContent?.minWordsLimit ?? null,
        aiAssistantEnabled: currentContent?.aiAssistantEnabled ?? false,
        aiAssistantSettingsUid: currentContent?.aiAssistantSettingsUid ?? null,
        isTemplate: currentContent?.isTemplate ?? false,
      },
    };

    this.form = payload as Form;
    this.formElementUpdated.emit(payload);
  }
}
